import * as Sentry from '@sentry/react'
import { get, isEmpty, pick } from 'lodash/fp'
import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import actions from '@/actions'
import { SCORM_ACTION_AUTH, SENTRY_SECTIONS } from '@/core/constants/constants'

import {
  scormAuthSelectors,
  scormConfigSelector,
} from '../selectors/scorm.selectors'
import { ScormAuthPayload } from '../types/ScormAuthPayload'
import detectStudentId from '../utils/detectStudentId'

export default function useScormAuth() {
  const dispatch = useDispatch()
  const fetched = useSelector(scormAuthSelectors.fetched)
  const error = useSelector(scormAuthSelectors.error)
  const errorStatus = useSelector(scormAuthSelectors.errorStatus)
  const data = useSelector(scormAuthSelectors.data)
  const scormConfig = useSelector(scormConfigSelector)

  // Id Masterplan is used as a SCORM package the code should fire up itself after it loads
  // inside an iframe. Then we post the first message signalling readiness to communicate and request
  // for authorisation. At this point Masterplan SCORM package can be called from any origin, but will
  // do the screening in the process message block, by calling auth REST API.
  useEffect(() => {
    window.parent?.postMessage({ type: SCORM_ACTION_AUTH }, '*')
  }, [])

  // Id Masterplan is used as a SCORM package the code should fire up itself after it loads
  // inside the iframe. Then we register message handler and get the referer.
  type ScormAuthData = {
    origin: string
    data: { type: string; payload: ScormAuthPayload }
  }
  useEffect(() => {
    const processMessage = async (event: ScormAuthData) => {
      const {
        origin,
        data: { type, payload },
      } = event

      const isDifferentOrigin = origin !== window.location.origin
      const isValidAuthAction = type === SCORM_ACTION_AUTH && !isEmpty(payload)

      if (isDifferentOrigin && isValidAuthAction) {
        const student_id = detectStudentId(
          pick(['student_id', 'learner_id'], payload),
        )

        const { client_id, config, location_hash } = payload
        dispatch(actions.scorm.setConfig(config))
        dispatch(actions.scorm.setOrigin(origin))

        Promise.resolve(
          dispatch(
            actions.scorm.auth.post({ client_id, location_hash, student_id }),
          ),
        ).catch((failedAction) => {
          // redux-axios-middleware rejects with the FAIL action wrapping the AxiosError
          const response = get('error.response', failedAction)
          Sentry.captureMessage('SCORM auth request failed', {
            level: 'error',
            tags: { section: SENTRY_SECTIONS.scorm },
            extra: {
              client_id,
              origin,
              has_student_id: Boolean(student_id),
              status: get('status', response),
              response: get('data', response),
            },
          })
        })
      }
    }

    window.addEventListener('message', processMessage)

    return () => window.removeEventListener('message', processMessage)
  }, [dispatch])

  return {
    config: scormConfig,
    data,
    error,
    errorStatus,
    fetched,
  }
}
